import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

function BrowseExamPage() {
  const { examId } = useParams()
  const navigate = useNavigate()
  const [questions, setQuestions] = useState([])
  const [answered, setAnswered] = useState({}) // { [questionId]: selectedAnswerId }
  const [loading, setLoading] = useState(true)
  const questionRefs = useRef({})

  useEffect(() => {
    axios({ method: 'GET', url: `exam/${examId}/browse` })
      .then(res => { setQuestions(res.data || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [examId])

  const handleSelect = (questionId, answerId) => {
    if (answered[questionId] !== undefined) return // already answered
    setAnswered(prev => ({ ...prev, [questionId]: answerId }))
  }

  const scrollTo = (qId) => {
    questionRefs.current[qId]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const answeredCount = Object.keys(answered).length
  const correctCount = questions.filter(q => {
    const selected = answered[q.id]
    if (selected === undefined) return false
    const correctAns = q.exam_question_answers?.find(a => a.trueAnswer === 1)
    return correctAns && correctAns.id === selected
  }).length

  if (loading) return (
    <div className='min-h-screen flex items-center justify-center'>
      <div className='w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin' />
    </div>
  )

  if (!questions.length) return (
    <div className='min-h-screen flex items-center justify-center text-slate-400'>Асуулт олдсонгүй</div>
  )

  return (
    <div className='min-h-screen bg-slate-50'>

      {/* Sticky header */}
      <div className='bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm'>
        <div className='max-w-3xl mx-auto px-4 h-14 flex items-center justify-between gap-4'>
          <button
            onClick={() => navigate(-1)}
            className='text-slate-500 hover:text-slate-800 text-sm flex items-center gap-1 shrink-0'
          >
            ← Буцах
          </button>
          <div className='flex items-center gap-3 text-sm'>
            <span className='text-slate-400'>Нийт: <b className='text-slate-700'>{questions.length}</b></span>
            <span className='text-slate-300'>|</span>
            <span className='text-slate-400'>Хариулсан: <b className='text-blue-600'>{answeredCount}</b></span>
            {answeredCount > 0 && (
              <>
                <span className='text-slate-300'>|</span>
                <span className='text-green-600 font-semibold'>{correctCount} зөв</span>
                <span className='text-red-500 font-semibold'>{answeredCount - correctCount} буруу</span>
              </>
            )}
          </div>
        </div>
        {/* Progress */}
        <div className='h-1 bg-slate-100'>
          <div
            className='h-1 bg-blue-500 transition-all duration-300'
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div className='max-w-3xl mx-auto px-4 py-6 space-y-5'>

        {questions.map((q, i) => {
          const selectedId = answered[q.id]
          const isAnswered = selectedId !== undefined
          const correctAns = q.exam_question_answers?.find(a => a.trueAnswer === 1)
          const isCorrect = isAnswered && correctAns?.id === selectedId

          return (
            <div
              key={q.id}
              ref={el => { questionRefs.current[q.id] = el }}
              className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all
                ${!isAnswered ? 'border-slate-200' : isCorrect ? 'border-green-300' : 'border-red-300'}`}
            >
              {/* Question header */}
              <div className={`px-5 py-3 flex items-center gap-3
                ${!isAnswered ? 'bg-blue-500' : isCorrect ? 'bg-green-500' : 'bg-red-500'}`}
              >
                <span className='w-8 h-8 rounded-full bg-white flex items-center justify-center text-sm font-bold text-slate-700 shrink-0'>
                  {i + 1}
                </span>
                <span className='text-white text-xs opacity-80'>{q.testType || 'Нэг сонголттой'}</span>
                {isAnswered && (
                  <span className='ml-auto text-white text-sm font-semibold'>
                    {isCorrect ? '✓ Зөв' : '✗ Буруу'}
                  </span>
                )}
              </div>

              {/* Question text */}
              <div className='px-5 py-4 border-b border-slate-100'>
                <div
                  className='text-slate-800 leading-relaxed font-medium'
                  dangerouslySetInnerHTML={{ __html: q.question }}
                />
              </div>

              {/* Answers */}
              <div className='px-5 py-4 space-y-2'>
                {(q.exam_question_answers || []).map((ans, idx) => {
                  const letter = String.fromCharCode(65 + idx)
                  const isSelected = selectedId === ans.id
                  const isCorrectAns = ans.trueAnswer === 1

                  let style = 'bg-slate-50 border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                  let circleStyle = 'bg-slate-100 text-slate-500'

                  if (isAnswered) {
                    if (isCorrectAns) {
                      style = 'bg-green-50 border-green-400 text-green-800'
                      circleStyle = 'bg-green-500 text-white'
                    } else if (isSelected) {
                      style = 'bg-red-50 border-red-400 text-red-700'
                      circleStyle = 'bg-red-500 text-white'
                    } else {
                      style = 'bg-slate-50 border-slate-200 text-slate-400'
                    }
                  }

                  return (
                    <div
                      key={ans.id}
                      onClick={() => handleSelect(q.id, ans.id)}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all select-none ${style}`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${circleStyle}`}>
                        {letter}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='text-sm leading-snug'>{ans.answer}</div>
                        {/* Explanation — show only on correct answer after answering */}
                        {isAnswered && isCorrectAns && ans.answerDescr && (
                          <div className='mt-2 text-xs text-green-700 bg-green-100 rounded-lg px-3 py-2 leading-relaxed border border-green-200'>
                            {ans.answerDescr}
                          </div>
                        )}
                      </div>
                      {isAnswered && isCorrectAns && (
                        <svg className='w-5 h-5 text-green-500 shrink-0 mt-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
                        </svg>
                      )}
                      {isAnswered && isSelected && !isCorrectAns && (
                        <svg className='w-5 h-5 text-red-500 shrink-0 mt-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M6 18L18 6M6 6l12 12' />
                        </svg>
                      )}
                    </div>
                  )
                })}

                {/* Not answered hint */}
                {!isAnswered && (
                  <div className='text-center text-xs text-slate-400 pt-1'>Хариулт сонгоно уу</div>
                )}
              </div>
            </div>
          )
        })}

        <button
          onClick={() => navigate(-1)}
          className='w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors'
        >
          Буцах
        </button>
      </div>

      {/* Floating question nav */}
      <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-20'>
        <div className='max-w-3xl mx-auto px-4 py-2'>
          <div className='flex flex-wrap gap-1 max-h-24 overflow-y-auto'>
            {questions.map((q, i) => {
              const isAns = answered[q.id] !== undefined
              const correctAns = q.exam_question_answers?.find(a => a.trueAnswer === 1)
              const isOk = isAns && correctAns?.id === answered[q.id]
              return (
                <button
                  key={q.id}
                  onClick={() => scrollTo(q.id)}
                  className={`w-8 h-8 rounded text-xs font-semibold transition-colors
                    ${!isAns ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      : isOk ? 'bg-green-500 text-white'
                      : 'bg-red-400 text-white'}`}
                >
                  {i + 1}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Bottom padding for nav */}
      <div className='h-20' />
    </div>
  )
}

export default BrowseExamPage
